import { useState, useMemo } from 'react';
import { X, AlertTriangle, Trash2, CheckCircle, Search, RefreshCw } from 'lucide-react';

/**
 * DuplicateCleanupModal
 * 
 * Scans trades for duplicates and allows bulk removal
 */
const DuplicateCleanupModal = ({
  darkMode,
  trades,
  accounts,
  onClose,
  onRemoveDuplicates
}) => {
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [removed, setRemoved] = useState(false);

  // Generate trade signature for duplicate detection
  const getTradeSignature = (trade) => {
    const entryRounded = typeof trade.entry === 'number' ? trade.entry.toFixed(2) : trade.entry;
    return `${trade.date}|${trade.symbol}|${trade.direction}|${entryRounded}|${trade.entryTime || ''}|${trade.quantity}|${trade.accountId || ''}`;
  };

  // Find duplicate groups
  const duplicateGroups = useMemo(() => {
    const signatureMap = new Map();
    
    // Group trades by signature
    trades.forEach(trade => {
      const sig = getTradeSignature(trade);
      if (!signatureMap.has(sig)) {
        signatureMap.set(sig, []);
      }
      signatureMap.get(sig).push(trade);
    });
    
    // Filter to only groups with duplicates (more than 1 trade)
    const groups = [];
    signatureMap.forEach((tradelist, sig) => {
      if (tradelist.length > 1) {
        // Sort by id (oldest first - assuming lower id = older)
        tradelist.sort((a, b) => {
          const aId = typeof a.id === 'number' ? a.id : parseInt(a.id) || 0;
          const bId = typeof b.id === 'number' ? b.id : parseInt(b.id) || 0;
          return aId - bId;
        });
        groups.push({
          signature: sig,
          original: tradelist[0],
          duplicates: tradelist.slice(1)
        });
      }
    });
    
    return groups;
  }, [trades]);

  // Get account name by ID
  const getAccountName = (accountId) => {
    const account = accounts?.find(a => a.id === accountId);
    return account?.name || accountId || 'Unknown';
  };

  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    const formatted = Math.abs(num).toFixed(2);
    return num >= 0 ? `+$${formatted}` : `-$${formatted}`;
  };

  // Toggle selection of a duplicate
  const toggleDuplicate = (tradeId) => {
    setSelectedDuplicates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tradeId)) {
        newSet.delete(tradeId);
      } else {
        newSet.add(tradeId);
      }
      return newSet;
    });
  };

  // Select all duplicates (not originals)
  const selectAllDuplicates = () => {
    const allDuplicateIds = new Set();
    duplicateGroups.forEach(group => {
      group.duplicates.forEach(dup => allDuplicateIds.add(dup.id));
    });
    setSelectedDuplicates(allDuplicateIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedDuplicates(new Set());
  };

  // Handle removal
  const handleRemove = () => {
    if (selectedDuplicates.size === 0) return;
    
    const idsToRemove = Array.from(selectedDuplicates);
    onRemoveDuplicates(idsToRemove);
    setRemoved(true);
    setShowConfirm(false);
    setSelectedDuplicates(new Set());
  };

  // Total duplicate count
  const totalDuplicates = duplicateGroups.reduce((sum, g) => sum + g.duplicates.length, 0);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${
        darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <Search className={darkMode ? 'text-yellow-400' : 'text-yellow-600'} size={24} />
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Duplicate Trade Cleanup
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {duplicateGroups.length > 0 
                  ? `Found ${totalDuplicates} duplicate(s) in ${duplicateGroups.length} group(s)`
                  : 'No duplicate trades found'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {removed ? (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="text-green-500 mb-4" size={48} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Duplicates Removed!
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Your trade journal has been cleaned up.
              </p>
              <button
                onClick={() => setRemoved(false)}
                className={`mt-4 flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <RefreshCw size={16} />
                Scan Again
              </button>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="text-green-500 mb-4" size={48} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                No Duplicates Found
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Your trade journal is clean!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Action buttons */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={selectAllDuplicates}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70'
                      : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                  }`}
                >
                  Select All Duplicates ({totalDuplicates})
                </button>
                {selectedDuplicates.size > 0 && (
                  <button
                    onClick={clearSelection}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      darkMode
                        ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                        : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    Clear Selection
                  </button>
                )}
              </div>

              {/* Duplicate groups */}
              {duplicateGroups.map((group, groupIndex) => (
                <div 
                  key={group.signature}
                  className={`rounded-lg border ${
                    darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className={`px-4 py-2 border-b ${
                    darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'
                  }`}>
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Group {groupIndex + 1}: {group.original.symbol} {group.original.direction?.toUpperCase()} on {group.original.date}
                    </span>
                    <span className={`ml-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      ({group.duplicates.length + 1} identical trades)
                    </span>
                  </div>
                  
                  <div className="p-2 space-y-1">
                    {/* Original trade (keep) */}
                    <div className={`flex items-center gap-3 p-2 rounded-lg ${
                      darkMode ? 'bg-green-900/20 border border-green-700/30' : 'bg-green-50 border border-green-200'
                    }`}>
                      <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                        darkMode ? 'bg-green-700 text-green-100' : 'bg-green-200 text-green-800'
                      }`}>
                        KEEP
                      </div>
                      <div className="flex-1 grid grid-cols-6 gap-2 text-sm">
                        <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                          {group.original.symbol}
                        </span>
                        <span className={`${
                          group.original.direction === 'long' ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {group.original.direction?.toUpperCase()}
                        </span>
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {group.original.entryTime || '-'}
                        </span>
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          Qty: {group.original.quantity}
                        </span>
                        <span className={parseFloat(group.original.pnl) >= 0 ? 'text-green-500' : 'text-red-500'}>
                          {formatCurrency(group.original.pnl)}
                        </span>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {getAccountName(group.original.accountId)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Duplicate trades (remove) */}
                    {group.duplicates.map(dup => (
                      <div 
                        key={dup.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedDuplicates.has(dup.id)
                            ? darkMode 
                              ? 'bg-red-900/30 border border-red-700/50' 
                              : 'bg-red-50 border border-red-300'
                            : darkMode
                              ? 'bg-gray-700/30 border border-gray-600/30 hover:bg-gray-700/50'
                              : 'bg-white border border-gray-200 hover:bg-gray-100'
                        }`}
                        onClick={() => toggleDuplicate(dup.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDuplicates.has(dup.id)}
                          onChange={() => toggleDuplicate(dup.id)}
                          className="w-4 h-4 rounded-sm"
                        />
                        <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                          darkMode ? 'bg-red-700/50 text-red-200' : 'bg-red-100 text-red-700'
                        }`}>
                          DUP
                        </div>
                        <div className="flex-1 grid grid-cols-6 gap-2 text-sm">
                          <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                            {dup.symbol}
                          </span>
                          <span className={`${
                            dup.direction === 'long' ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {dup.direction?.toUpperCase()}
                          </span>
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                            {dup.entryTime || '-'}
                          </span>
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                            Qty: {dup.quantity}
                          </span>
                          <span className={parseFloat(dup.pnl) >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {formatCurrency(dup.pnl)}
                          </span>
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {getAccountName(dup.accountId)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!removed && duplicateGroups.length > 0 && (
          <div className={`flex items-center justify-between p-4 border-t ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {selectedDuplicates.size > 0 
                ? `${selectedDuplicates.size} trade(s) selected for removal`
                : 'Select duplicates to remove'
              }
            </div>
            
            {showConfirm ? (
              <div className="flex items-center gap-2">
                <span className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  <AlertTriangle size={16} className="inline mr-1" />
                  This cannot be undone!
                </span>
                <button
                  onClick={() => setShowConfirm(false)}
                  className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                      : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemove}
                  className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 flex items-center gap-2 ${
                    darkMode
                      ? 'bg-red-500/25 border-red-400/50 text-red-50 ring-1 ring-inset ring-red-400/25 hover:bg-red-500/35 hover:border-red-400/70 hover:shadow-[0_0_18px_rgba(239,68,68,0.3)]'
                      : 'bg-red-100 border-red-300 text-red-800 hover:bg-red-200 hover:border-red-400'
                  }`}
                >
                  <Trash2 size={16} />
                  Confirm Delete ({selectedDuplicates.size})
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={selectedDuplicates.size === 0}
                className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'bg-red-500/25 border-red-400/50 text-red-50 ring-1 ring-inset ring-red-400/25 hover:bg-red-500/35 hover:border-red-400/70 hover:shadow-[0_0_18px_rgba(239,68,68,0.3)]'
                    : 'bg-red-100 border-red-300 text-red-800 hover:bg-red-200 hover:border-red-400'
                }`}
              >
                <Trash2 size={16} />
                Remove Selected ({selectedDuplicates.size})
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DuplicateCleanupModal;
